import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  HourglassEmpty,
  Refresh,
  Logout,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const VerificationPending = () => {
  const navigate = useNavigate();
  const { userStatus, refreshUser, logout } = useAuth();

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Check if user is already verified
  useEffect(() => {
    if (userStatus === 'verified') {
      navigate('/dashboard');
    }
  }, [userStatus, navigate]);

  const handleCheckStatus = async () => {
    setLoading(true);
    setMessage('Checking status...');

    try {
      await refreshUser();
      const status = localStorage.getItem('userStatus');
      if (status === 'verified') {
        navigate('/dashboard');
      } else {
        setMessage('Your account is still pending verification. Please wait for admin approval.');
      }
    } catch (error) {
      console.error('Status check error:', error);
      setMessage('Failed to check status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #006994 0%, #00acc1 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <HourglassEmpty sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />

          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Verification Pending
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Your documents have been submitted successfully. Your registration is
            currently under review.
          </Typography>

          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              Our team will get in touch with you shortly to explain the payment process and
              further steps to complete your registration.
            </Typography>
          </Alert>

          {message && (
            <Typography
              variant="body2"
              color={message.includes('failed') ? 'error' : 'info'}
              sx={{ mb: 2 }}
            >
              {message}
            </Typography>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
              onClick={handleCheckStatus}
              disabled={loading}
              fullWidth
            >
              {loading ? 'Checking...' : 'Check Verification Status'}
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<Logout />}
              onClick={handleLogout}
              fullWidth
            >
              Logout
            </Button>
          </Box>
        </Paper>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="white">
            © 2026 Friends Services. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default VerificationPending;